import React, { useState, useCallback, useEffect } from 'react';
import { Slider, Range, FormControl, Box, Checkbox } from '@landr/maestro';
import { t } from '@lingui/macro';
import { BpmInputs } from './BpmInputs';

export const MinBpm = 40;
export const MaxBpm = 240;
export const DefaultRange: Range = { min: 40, max: 240 };
export const DefaultSingleBpm: Range = { min: 120, max: 120 };
export const BpmOffset = 20;

export interface BpmFilterProps {
    value: [number, number] | [null, null];
    onChange: (range: [number, number | undefined]) => void;
}

export interface useBpmFilterResult {
    defaultRange: Range;
    sliderValue: Range;
    hasRange: boolean;
    updateBpm: (value: number | Range) => void;
    onSliderChangeEnd: (value: number | Range) => void;
    onInputChange: ({ min, max }: Partial<Range>) => void;
    toggleHasRange: () => void;
}

export const useBpmFilter = ({ value, onChange }: BpmFilterProps) => {
    const [activeMin, activeMax] = value;
    const hasUserInput = activeMin !== null && activeMax !== null;
    const defaultRange = hasUserInput
        ? { min: activeMin, max: activeMax }
        : DefaultRange;
    const defaultHasRange = hasUserInput ? activeMin !== activeMax : true;

    const [bpmRange, setBpmRange] = useState<Range>(defaultRange);
    const [hasRange, setHasRange] = useState(defaultHasRange);

    const normalizeToRange = (value: number | Range): Range => {
        return typeof value === 'number' ? { min: value } : value;
    };

    const sliderValue: Range = hasRange
        ? { min: bpmRange.min, max: bpmRange.max ?? bpmRange.min }
        : { min: bpmRange.min };

    const updateBpm = useCallback(
        (value: number | Range) => {
            setBpmRange(normalizeToRange(value));
        },
        [setBpmRange],
    );

    const reset = useCallback(() => {
        setBpmRange(DefaultRange);
        setHasRange(true);
    }, [setBpmRange]);

    useEffect(() => {
        const [min, max] = value;

        if (min === null && max === null) {
            reset();
        }
    }, [value, reset]);

    const transposeRange = useCallback(
        (hasRange: boolean, hasActiveFilter: boolean) => {
            if (!hasActiveFilter && hasRange) {
                setBpmRange(DefaultRange);
            } else if (!hasActiveFilter && !hasRange) {
                setBpmRange(DefaultSingleBpm);
            } else if (hasRange) {
                let minRange = bpmRange.min - BpmOffset;
                let maxRange = bpmRange.min + BpmOffset;

                if (minRange < MinBpm) {
                    minRange = MinBpm;
                    maxRange = MinBpm + BpmOffset * 2;
                } else if (maxRange > MaxBpm) {
                    minRange = MaxBpm - BpmOffset * 2;
                    maxRange = MaxBpm;
                }
                setBpmRange({ min: minRange, max: maxRange });
                onChange([minRange, maxRange]);
            } else {
                let singleValue = bpmRange.min;

                if (
                    bpmRange.max !== undefined &&
                    bpmRange.max !== bpmRange.min
                ) {
                    singleValue = Math.round((bpmRange.min + bpmRange.max) / 2);
                }

                if (singleValue > MaxBpm) {
                    singleValue = MaxBpm;
                } else if (singleValue < MinBpm) {
                    singleValue = MinBpm;
                }

                setBpmRange({ min: singleValue });
                onChange([singleValue, undefined]);
            }
        },
        [bpmRange, setBpmRange, onChange],
    );

    const toggleHasRange = useCallback(() => {
        const newValue = !hasRange;

        setHasRange(newValue);
        const [activeMin, activeMax] = value;
        const hasActiveBpmFilter = activeMin !== null && activeMax !== null;

        transposeRange(newValue, hasActiveBpmFilter);
    }, [value, hasRange, setHasRange, transposeRange]);

    const onInputChange = useCallback(
        ({ min, max }: Partial<Range>) => {
            const range = {
                min: min ?? bpmRange.min,
                max: max ?? bpmRange.max,
            };

            setBpmRange(range);
            onChange(
                hasRange ? [range.min, range.max] : [range.min, undefined],
            );
        },
        [hasRange, bpmRange, setBpmRange, onChange],
    );

    const onSliderChangeEnd = useCallback(
        (value: number | Range) => {
            const range = normalizeToRange(value);

            setBpmRange(range);

            onChange(
                hasRange ? [range.min, range.max] : [range.min, undefined],
            );
        },
        [onChange, hasRange],
    );

    return {
        defaultRange,
        sliderValue,
        hasRange,
        updateBpm,
        onSliderChangeEnd,
        onInputChange,
        toggleHasRange,
    };
};

export const BpmFilter: React.FC<BpmFilterProps> = (props) => {
    const {
        sliderValue,
        hasRange,
        updateBpm,
        onSliderChangeEnd,
        onInputChange,
        toggleHasRange,
    } = useBpmFilter(props);

    return (
        <>
            <Box>
                <Slider
                    key={`slider-${hasRange ? 'range' : 'single'}`}
                    min={MinBpm}
                    max={MaxBpm}
                    value={sliderValue}
                    onChange={updateBpm}
                    onChangeEnd={onSliderChangeEnd}
                />
            </Box>
            <Box my="lg">
                <BpmInputs
                    min={MinBpm}
                    max={MaxBpm}
                    value={sliderValue}
                    onChange={onInputChange}
                    showMaxInput={hasRange}
                />
            </Box>
            <FormControl>
                <Checkbox
                    size="sm"
                    label={t`BPM range`}
                    isChecked={hasRange}
                    onChange={toggleHasRange}
                    paddingLeft="none"
                />
            </FormControl>
        </>
    );
};
